import { Box, Button, InputBase, SxProps } from '@mui/material';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { useEffect, useMemo, useState } from 'react';
import { IStyles } from '../../../commonUtils/styleUtils';

export type QuantityButtonV2Props = {
  value: number;
  onChange?: (nextValue: number) => void;
  onIncrement?: () => void;
  onDecrement?: () => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  rootStyles?: SxProps;
};

export const QuantityButtonV2 = (props: QuantityButtonV2Props) => {
  const {
    value,
    onChange,
    onIncrement,
    onDecrement,
    min,
    max,
    disabled,
    rootStyles,
  } = props;

  // ----------------------------------------------------

  const FONT_SIZE = { xs: 18, md: 35, lg: 26 };

  const styles: IStyles = {
    root: {
      width: { xs: '100%', md: pxTovW(170), lg: pxTovW(150) },
      height: { xs: pxToRem(50), md: pxTovW(75), lg: pxTovW(60) },
      boxShadow: { xs: '0px 3px 6px #0000000A', md: '0px 3px 6px #0000000A' },
      border: '1px solid #007CDC66',
      borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      overflow: 'hidden',
      backgroundColor: '#fff',
      boxSizing: 'border-box',
      paddingX: { xs: pxToRem(0), md: pxTovW(0) },
    },
    addButton: {
      width: '100%',
      height: '100%',
      borderRadius: 0,
      boxShadow: 'none',
      fontSize: {
        xs: pxToRem(FONT_SIZE.xs),
        md: pxTovW(FONT_SIZE.md),
        lg: pxTovW(FONT_SIZE.lg),
      },
      fontWeight: 600,
      textTransform: 'none',
    },
    stepButton: {
      width: { xs: pxToRem(40), md: pxTovW(35) },
      height: '100%',
      minWidth: { xs: pxToRem(40), md: pxTovW(35) },
      flexShrink: 0,
      padding: 0,
      borderRadius: 0,
      fontSize: {
        xs: pxToRem(FONT_SIZE.xs),
        md: pxTovW(FONT_SIZE.md),
        lg: pxTovW(FONT_SIZE.lg),
      },
      fontWeight: 600,
    },
    inputWrapper: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: '6px',
    },
    inputBase: {
      width: '100%',
      textAlign: 'center',
      fontSize: {
        xs: pxToRem(FONT_SIZE.xs),
        md: pxTovW(FONT_SIZE.md),
        lg: pxTovW(FONT_SIZE.lg),
      },
      fontWeight: 600,
      '& input': {
        textAlign: 'center',
        fontSize: {
          xs: pxToRem(FONT_SIZE.xs),
          md: pxTovW(FONT_SIZE.md),
          lg: pxTovW(FONT_SIZE.lg),
        },
        fontWeight: 600,
        padding: 0,
      },
      '& input::placeholder': {
        opacity: 1,
        color: 'secondary.main',
      },
    },
  };

  // ----------------------------------------------------

  const [draft, setDraft] = useState<string>(() =>
    value === 0 ? '' : String(value)
  );
  const effectiveMin = useMemo(
    () => (typeof min === 'number' ? min : 0),
    [min]
  );
  const effectiveMax = useMemo(
    () => (typeof max === 'number' ? max : undefined),
    [max]
  );
  const canDecrement = !disabled && value > effectiveMin;
  const canIncrement =
    !disabled && (typeof effectiveMax !== 'number' || value < effectiveMax);

  // ----------------------------------------------------

  const clamp = (n: number) => {
    let next = Number.isFinite(n) ? Math.trunc(n) : 0;
    next = Math.max(0, next);
    next = Math.max(effectiveMin, next);
    if (typeof effectiveMax === 'number') next = Math.min(effectiveMax, next);
    return next;
  };

  // ----------------------------------------------------

  const setDraftFromValue = (nextValue: number) => {
    setDraft(nextValue === 0 ? '' : String(nextValue));
  };

  // ----------------------------------------------------

  const commit = (raw: string) => {
    const nextValue = raw === '' ? 0 : clamp(parseInt(raw, 10));
    setDraftFromValue(nextValue);
    if (nextValue !== value) onChange?.(nextValue);
  };

  // ----------------------------------------------------

  const handleDraftChange = (next: string) => {
    if (next === '') {
      setDraft('');
      return;
    }
    if (!/^\d+$/.test(next)) return;

    const parsed = parseInt(next, 10);
    if (typeof effectiveMax === 'number' && parsed > effectiveMax) {
      setDraftFromValue(effectiveMax);
      if (effectiveMax !== value) onChange?.(effectiveMax);
      return;
    }

    setDraft(next);
  };

  // ----------------------------------------------------

  const handleIncrement = () => {
    if (disabled || !canIncrement) return;
    if (onIncrement) {
      onIncrement();
      return;
    }
    onChange?.(clamp(value + 1));
  };

  // ----------------------------------------------------

  const handleDecrement = () => {
    if (disabled || !canDecrement) return;
    if (onDecrement) {
      onDecrement();
      return;
    }
    onChange?.(clamp(value - 1));
  };

  // ----------------------------------------------------

  // Keep the input in sync with external value updates.
  useEffect(() => {
    setDraft(value === 0 ? '' : String(value));
  }, [value]);

  // ----------------------------------------------------

  let rootSx = { ...styles.root };
  if (rootStyles) rootSx = { ...rootSx, ...rootStyles };

  return (
    <Box sx={rootSx}>
      {value === 0 ? (
        <Button
          type="button"
          onClick={handleIncrement}
          disabled={!canIncrement}
          aria-label="Add quantity"
          sx={{
            ...styles.addButton,
            color: canIncrement ? 'secondary.main' : 'grey.400',
          }}
        >
          Add
        </Button>
      ) : (
        <>
          <Button
            type="button"
            onClick={handleDecrement}
            disabled={!canDecrement}
            aria-label="Decrement quantity"
            sx={{
              ...styles.stepButton,
              color: canDecrement ? 'secondary.main' : 'grey.400',
            }}
          >
            -
          </Button>

          <Box sx={styles.inputWrapper}>
            <InputBase
              value={draft}
              placeholder="Add"
              onChange={(e) => handleDraftChange(e.target.value)}
              onBlur={() => commit(draft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commit(draft);
                  (e.target as HTMLInputElement).blur();
                }
              }}
              disabled={disabled}
              inputProps={{ 'aria-label': 'Quantity input' }}
              sx={
                disabled
                  ? {
                      ...styles.inputBase,
                      color: 'grey.400',
                      '& input::placeholder': { opacity: 1, color: 'grey.400' },
                    }
                  : { ...styles.inputBase, color: 'secondary.main' }
              }
            />
          </Box>

          <Button
            type="button"
            onClick={handleIncrement}
            disabled={!canIncrement}
            aria-label="Increment quantity"
            sx={{
              ...styles.stepButton,
              color: canIncrement ? 'secondary.main' : 'grey.400',
            }}
          >
            +
          </Button>
        </>
      )}
    </Box>
  );
};
